import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const inputCls = 'w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C41E3A]';

const Choice = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`flex-1 px-3 py-2 rounded-lg text-sm border transition-colors ${active ? 'border-[#C41E3A] bg-[#C41E3A]/15 text-white' : 'border-zinc-700 text-zinc-400 hover:text-white'}`}
  >
    {children}
  </button>
);

export default function ActivateProfileModal({ profile, onClose, onDone }) {
  const [addons, setAddons] = useState([]);
  const [form, setForm] = useState({ plan: '1_year', tier: 'basic', addons: [], payment_reference: '', payment_method: 'bank_transfer', amount: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/addons`).then((r) => setAddons(r.data.addons || [])).catch(() => {});
  }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleAddon = (key) => set({ addons: form.addons.includes(key) ? form.addons.filter((k) => k !== key) : [...form.addons, key] });
  const chooseTier = (tier) => set({ tier, plan: tier === 'all_access' ? '1_year' : form.plan, addons: tier === 'all_access' ? [] : form.addons });

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/profiles/${profile.id}/activate-subscription`, {
        ...form, amount: parseFloat(form.amount) || 0,
      }, { withCredentials: true });
      toast.success(`${profile.company_name} activated`);
      onDone();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Activation failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" data-testid="activate-profile-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white">Activate {profile.company_name}</h2>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="activate-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-zinc-500 mb-2">Plan type</p>
        <div className="flex gap-2 mb-4">
          <Choice active={form.tier === 'basic'} onClick={() => chooseTier('basic')} testId="activate-tier-basic">Basic</Choice>
          <Choice active={form.tier === 'all_access'} onClick={() => chooseTier('all_access')} testId="activate-tier-all-access">All-Access</Choice>
        </div>
        <p className="text-xs text-zinc-500 mb-2">Duration</p>
        <div className="flex gap-2 mb-4">
          <Choice active={form.plan === '6_months'} onClick={() => form.tier === 'basic' && set({ plan: '6_months' })} testId="activate-plan-6m">6 months{form.tier === 'all_access' ? ' (n/a)' : ''}</Choice>
          <Choice active={form.plan === '1_year'} onClick={() => set({ plan: '1_year' })} testId="activate-plan-1y">1 year</Choice>
        </div>
        {form.tier === 'basic' && (
          <div className="mb-4" data-testid="activate-addons">
            <p className="text-xs text-zinc-500 mb-2">Paid Pro features (12 months each)</p>
            <div className="space-y-2">
              {addons.map((a) => (
                <label key={a.key} className="flex items-center gap-3 text-sm text-zinc-300 cursor-pointer">
                  <input type="checkbox" className="accent-[#C41E3A]" checked={form.addons.includes(a.key)} onChange={() => toggleAddon(a.key)} data-testid={`activate-addon-${a.key}`} />
                  {a.name} <span className="text-zinc-500">₹{a.price_inr.toLocaleString('en-IN')}</span>
                </label>
              ))}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <input className={inputCls} placeholder="Payment reference" value={form.payment_reference} onChange={(e) => set({ payment_reference: e.target.value })} data-testid="activate-payment-ref" />
          <input className={inputCls} type="number" placeholder="Amount received (₹)" value={form.amount} onChange={(e) => set({ amount: e.target.value })} data-testid="activate-amount" />
          <select className={`${inputCls} col-span-2`} value={form.payment_method} onChange={(e) => set({ payment_method: e.target.value })} data-testid="activate-payment-method">
            <option value="bank_transfer">Bank transfer</option>
            <option value="upi">UPI</option>
            <option value="cheque">Cheque</option>
            <option value="cash">Cash</option>
            <option value="complimentary">Complimentary</option>
          </select>
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={saving}
          data-testid="activate-submit-btn"
          className="w-full bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
        >
          <CheckCircle className="w-4 h-4" /> {saving ? 'Activating...' : 'Activate profile'}
        </button>
      </div>
    </div>
  );
}
